// eslint-disable-next-line import/named
import { TListenerApi, createListenerMiddleware } from '@myua/rtk-helper';
import { BaseActionCreator, PayloadAction } from '@reduxjs/toolkit/dist/createAction';
import { mergeSignals } from './utils/deriveAbortController';

type LAPI<StoreShape> = Pick<TListenerApi<StoreShape>, 'take' | 'signal'>;

export async function _take<
  StoreShape,
  P1,
  T1 extends string,
  P2,
  T2 extends string,
  P3,
  T3 extends string,
  P4,
  T4 extends string,
>(
  api: LAPI<StoreShape>,
  action1: BaseActionCreator<P1, T1>,
  action2: BaseActionCreator<P2, T2>,
  action3: BaseActionCreator<P3, T3>,
  action4: BaseActionCreator<P4, T4>,
): Promise<
  PayloadAction<P1, T1> | PayloadAction<P2, T2> | PayloadAction<P3, T3> | PayloadAction<P4, T4>
>;
export async function _take<
  StoreShape,
  P1,
  T1 extends string,
  P2,
  T2 extends string,
  P3,
  T3 extends string,
>(
  api: LAPI<StoreShape>,
  action1: BaseActionCreator<P1, T1>,
  action2: BaseActionCreator<P2, T2>,
  action3: BaseActionCreator<P3, T3>,
): Promise<PayloadAction<P1, T1> | PayloadAction<P2, T2> | PayloadAction<P3, T3>>;
export async function _take<StoreShape, P1, T1 extends string, P2, T2 extends string>(
  api: LAPI<StoreShape>,
  action1: BaseActionCreator<P1, T1>,
  action2: BaseActionCreator<P2, T2>,
): Promise<PayloadAction<P1, T1> | PayloadAction<P2, T2>>;
export async function _take<StoreShape, P1, T1 extends string>(
  api: LAPI<StoreShape>,
  action1: BaseActionCreator<P1, T1>,
): Promise<PayloadAction<P1, T1>>;
export async function _take<StoreShape, P1, T1 extends string>(
  api: LAPI<StoreShape>,
  ...actionsToTake: BaseActionCreator<P1, T1>[]
) {
  const ret = await api.take((actionDispatched) =>
    actionsToTake.some((a) => a.type === actionDispatched.type),
  );
  return ret[0] as PayloadAction<P1, T1>;
}

export function _select<StoreShape, SelectorReturn>(
  api: Pick<TListenerApi<StoreShape>, 'getState'>,
  selector: (state: StoreShape) => SelectorReturn,
) {
  return selector(api.getState());
}

export function _fork<StoreShape, API extends Pick<TListenerApi<StoreShape>, 'fork' | 'signal'>>(
  lapi: API,
  forkFn: (lapi: API) => Promise<void>,
) {
  const task = lapi.fork(async (forkapi) => {
    const _lapi: API = {
      ...lapi,
      signal: mergeSignals(lapi.signal, forkapi.signal),
    };
    return await forkFn(_lapi);
  });
  return task;
}

export type ListenerMiddleware<StoreShape> = ReturnType<
  typeof createListenerMiddleware<StoreShape>
>;

export type ListenerApiSafe<StoreShape> = Omit<
  TListenerApi<StoreShape>,
  'cancelActiveListeners' | 'unsubscribe' | 'subscribe'
>;
